import {Modal, Space, Spin, Tooltip} from 'antd';
import React, {useMemo, useState} from 'react';

import {useDispatch, useIntl} from 'umi';
import {LeadModel} from '@/typings/models/Lead';
import AgentName from '@/components/Agent/AgentName';
import {checkNullString} from '@/utils/prettyname';
import styles from './index.less';
import LeadAssigneeDropdownFilters from '@/components/Filters/LeadAssigneeDropdownFilters';
import {useHasAccess} from '@/utils/hooks';
import PERMISSIONS from '@/constants/permissions';
import classNames from 'classnames';
import {ServiceRequestModel} from '@/typings/models/ServiceRequest';
import {getServiceRequestAssignee} from '@/components/Kanban/ServiceRequests/Calendar/utils';
import {editCalendarEventRequest, editServiceRequestRequest} from '@/services/api/serviceRequest';
import pick from 'lodash/pick';
import {fetchSingleLeadDetails} from '@/services/api/lead';
import {formatToISOStringWithOffset} from '@/utils/date';
import {isRecurringEvent} from '@/utils/recurrence';
import {useRecurrenceScope} from '@/components/Kanban/ServiceRequests/Calendar/Recurrence/useRecurrenceScope';

const LeadAssigneeOverview = ({
                                lead,
                                readonly,
                                isTable,
                                serviceRequest,
                                leadContextMenuProps = {},
                                showAssigneeName,
                              }: {
  lead?: LeadModel,
  serviceRequest?: ServiceRequestModel,
  isTable?: boolean,
  readonly?: boolean,
  showAssigneeName?: boolean,
  avatarClassName?: string,
  leadContextMenuProps?: {}
}) => {

  const dispatch = useDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const intl = useIntl()
  const { askRecurrenceScope, recurrenceScopeDrawer } = useRecurrenceScope();
  const mode = serviceRequest ? 'serviceRequest' : 'lead';
  const colleaguesBranchId = lead?.default_branch?.id;
  const colleaguesServiceId = lead?.default_branch_service?.id;
  const colleaguesOf = useMemo(
    () => (mode === 'lead' && colleaguesServiceId
      ? { branchId: colleaguesBranchId, branchServiceId: colleaguesServiceId }
      : undefined),
    [mode, colleaguesBranchId, colleaguesServiceId],
  );

  const assignee = mode === 'serviceRequest' ? getServiceRequestAssignee(serviceRequest) : lead?.assignee;
  const assigneeName = assignee ? `${checkNullString(assignee.firstname)} ${checkNullString(assignee.lastname)}` : '';

  const hasAssignLeadsAccess = useHasAccess(mode === 'lead' ? [PERMISSIONS.Leads.ASSIGN, PERMISSIONS.BUSINESS_PARTNER] : [PERMISSIONS.ServiceRequests.EDIT, PERMISSIONS.BUSINESS_PARTNER]);
  const hasCalendarEventsCreateForOthers = useHasAccess([PERMISSIONS.CalendarEvents.CREATE_FOR_OTHERS, PERMISSIONS.BUSINESS_PARTNER]);

  const canAssignCalendarEvent = serviceRequest?.blocker ? hasCalendarEventsCreateForOthers : true;
  const canAssign = canAssignCalendarEvent && (hasAssignLeadsAccess || !assignee?.id);

  showAssigneeName = showAssigneeName || isTable;

  const isCalendarEventReadonly = serviceRequest?.blocker && !hasCalendarEventsCreateForOthers;
  if (readonly || isCalendarEventReadonly) {
    if (!assignee) return <> - </>;
    return (
      <Tooltip placement={'topLeft'} trigger={['hover']}
               title={<div className={styles.assigneeTooltip}>
                 <span>{assigneeName}</span>
                 {assignee?.email && <div><small>{assignee?.email}</small></div>}
               </div>}>
        <Space>
          <AgentName agent={assignee} />
        </Space>
      </Tooltip>
    );
  }

  const handleMeetingAssigneeChange = async (rawAssigneeId, rawAssigneeObject) => {
    const newAssigneeId = rawAssigneeId ?? null;
    const newAssigneeObject = rawAssigneeObject ?? null;
    let applyTo;
    // Recurring custom event: ask which occurrences the assignee change applies to.
    if (serviceRequest?.blocker && isRecurringEvent(serviceRequest as any)) {
      const scope = await askRecurrenceScope({ action: 'edit' });
      if (!scope) return;
      applyTo = scope;
    }
    dispatch({
      type: 'serviceRequest/updateCalendarEvent',
      payload: {
        event: {
          id: serviceRequest?.id,
          // Calendar events carry their assignee on `assignee`; `agent` is the
          // appointment field (see getServiceRequestAssignee).
          ...(serviceRequest?.blocker
            ? { assignee: newAssigneeObject, assigneeId: newAssigneeId }
            : { agent: newAssigneeObject }),
        },
      },
    });
    if (serviceRequest.blocker) {
      // TODO Change this implementation when PATCH is provided by backend.
      editCalendarEventRequest({
        params: {
          calendarEventId: serviceRequest.id,
        },
        data: {
          ...pick(serviceRequest, ['title', 'description', 'color']),
          // Same payload as a calendar drag/drop: local time with offset.
          start: formatToISOStringWithOffset(new Date(serviceRequest.utc_start_time)),
          end: formatToISOStringWithOffset(new Date(serviceRequest.utc_end_time)),
          assignee: newAssigneeId,
          ...(applyTo ? { apply_to: applyTo } : {}),
        },
      });
    } else {
      editServiceRequestRequest({
        params: {
          serviceRequestId: serviceRequest?.id,
        },
        data: {
          assignee: newAssigneeId,
        },
      });
    }
  };

  const handleLeadAssigneeChange = async (newAssigneeId, assignee) => {
    const performAssignment = (query = {}) => {
      dispatch({
        type: 'leads/edit',
        payload: {
          leadId: lead?.id,
          assignee_id: newAssigneeId,
          assignee,
          query,
          fetchDetails: true
        },
      });
    };

    const checkUpcomingMeetingsToAssign = (_lead = lead) => {
      if (!_lead.hasOwnProperty('upcoming_service_requests_count')) {
        setLoading(true);
        return fetchSingleLeadDetails({
          params: {
            leadId: lead.id,
          },
        }).then((response) => {
          setLoading(false);
          const leadDetails = response?.data?.data || {};
          if (leadDetails.hasOwnProperty('upcoming_service_requests_count')) {
            checkUpcomingMeetingsToAssign(leadDetails);
          }
        });
      }
      const upcomingServiceRequestsCount = _lead.upcoming_service_requests_count;
      if (parseInt(upcomingServiceRequestsCount) > 0) {
        Modal.confirm({
          maskClosable: true,
          closeIcon: true,
          title: intl.formatMessage({
            id: 'pages.leads.assign_upcoming_appointments.title',
            defaultMessage: 'Upcoming meetings for this project'
          }),
          content: intl.formatMessage({
            id: 'pages.leads.assign_upcoming_appointments.description',
            defaultMessage: 'This project has upcoming appointments. Since those appointments are already assigned to some agents, you can assign the appointments too.'
          }),
          okText: intl.formatMessage({
            id: 'pages.leads.assign_upcoming_appointments.assign_meetings',
            defaultMessage: 'Assign meetings',
          }),
          cancelText: intl.formatMessage({
            id: 'pages.leads.assign_upcoming_appointments.assign_project',
            defaultMessage: 'Assign project only',
          }),
          onOk(){
            performAssignment({
              update_upcoming_appointments: '1'
            });
          },
          onCancel(){
            performAssignment()
          }
        })
      } else {
        performAssignment();
      }
    };

    checkUpcomingMeetingsToAssign();

  };
  const handleAssigneeChange = (newAssigneeId, newAssigneeObject) => {
    if (mode === 'lead')
      handleLeadAssigneeChange(newAssigneeId, newAssigneeObject);
    else
      handleMeetingAssigneeChange(newAssigneeId, newAssigneeObject);
  };
  return <>
    <Spin spinning={loading}>
    <LeadAssigneeDropdownFilters filterMode={'select'}
                                 onlyAssignable
                                 type={'dropdown'}
                                 style={isTable ? { minWidth: '100%' } : {}}
                                 className={classNames(styles.leadAssigneeOverview, {
                                   [styles.disabledAssignee]: !canAssign,
                                 })}
                                 value={assignee?.id}
                                 assigneeObject={assignee}
                                 hideDisabled={true}
                                 colleaguesOf={colleaguesOf}
                                 selectProps={{
                                   variant: 'borderless',
                                   disabled: !canAssign,
                                 }}
                                 onChange={handleAssigneeChange} />
  </Spin>
    {recurrenceScopeDrawer}
  </>

};

export default LeadAssigneeOverview;
